/**
 * The dashboard's boards that are also screens of their own.
 *
 * The systems dashboard carries thirty boards behind its own group and board
 * navigation. Most of them — visual analytics, maintenance, SMS, the
 * certification registers, the business boards — were reachable only by
 * opening the dashboard and clicking through two rows of tabs, and a person
 * looking down the menu could not see they existed. Each one listed here gets
 * a route and a menu entry; the route opens the dashboard on that board.
 *
 * `view` is the id in the demo's DASHVIEWS. This file has no imports so the
 * menu can list the screens without loading the domain.
 */
export interface DashboardScreen {
  /** the DASHVIEWS id the route opens */
  view: string;
  to: string;
  label: string;
  /** SVG path data for the 24×24 icon */
  icon: string;
}

export const DASHBOARD_SCREENS: readonly DashboardScreen[] = [
  /* facilities */
  {
    view: "fac",
    to: "/facilities",
    label: "Airport facilities",
    icon: "M3 21h18M5 21V9l7-6 7 6v12M9 21v-7h6v7M9 11h.01M15 11h.01",
  },
  {
    view: "afinsp",
    to: "/airfield-inspections",
    label: "Airfield inspections",
    icon: "M3 17h18M6 17l2-8h8l2 8M10 9V5h4v4M8 21h8",
  },
  { view: "systems", to: "/systems", label: "Systems condition", icon: "M4 6h16v12H4zM8 18v3h8v-3M7 13l3-3 3 2 4-4" },
  { view: "docs", to: "/documents", label: "Documents", icon: "M6 3h9l5 5v13H6zM15 3v5h5M9 13h6M9 17h6" },
  /* engineering */
  {
    view: "viz",
    to: "/visual-analytics",
    label: "Visual analytics",
    icon: "M3 6h18v12H3zM3 12h18M9 6v12M15 6v12M12 9h.01M12 15h.01",
  },
  {
    view: "maint",
    to: "/maintenance",
    label: "Maintenance",
    icon: "M15 3a5 5 0 00-6 6l-6 6 3 3 6-6a5 5 0 006-6l-2 2-2-1-1-2z",
  },
  {
    view: "maintrep",
    to: "/maintenance-reporting",
    label: "Maintenance reporting",
    icon: "M4 20h16M6 20V12M10 20V8M14 20v-6M18 20V4M6 12l4-4 4 2 4-6",
  },
  /* operations */
  {
    view: "ops",
    to: "/operations",
    label: "Operations",
    icon: "M2 12h20M12 2c3 3 4 6 4 10s-1 7-4 10c-3-3-4-6-4-10s1-7 4-10z",
  },
  {
    view: "pax",
    to: "/passengers",
    label: "Passenger",
    icon: "M12 4a3 3 0 110 6 3 3 0 010-6zM5 21v-2a5 5 0 015-5h4a5 5 0 015 5v2",
  },
  {
    view: "stake",
    to: "/stakeholders",
    label: "Stakeholders",
    icon: "M8 5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM16 5a2.5 2.5 0 110 5 2.5 2.5 0 010-5zM2 19v-1a4 4 0 014-4h4a4 4 0 014 4v1M14 14h2a4 4 0 014 4v1",
  },
  /* safety and compliance */
  {
    view: "safety",
    to: "/safety",
    label: "Safety",
    icon: "M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6zM12 8v4M12 15v1",
  },
  { view: "sms", to: "/sms", label: "SMS", icon: "M4 4h16v11H9l-5 4zM8 8h8M8 11h5" },
  {
    view: "compliance",
    to: "/compliance-framework",
    label: "Compliance framework",
    icon: "M6 3h12v18H6zM9 8h6M9 12h6M9 16l1.5 1.5L14 14",
  },
  {
    view: "library",
    to: "/standards",
    label: "Standards library",
    icon: "M4 19V5a2 2 0 012-2h13v14H6a2 2 0 000 4h13M8 7h7M8 10h5",
  },
  /* certification */
  {
    view: "cert",
    to: "/certification",
    label: "Certification overview",
    icon: "M12 3l2 4 4.5.5-3.3 3.2.8 4.5L12 13l-4 2.2.8-4.5L5.5 7.5 10 7zM8 21l4-3 4 3v-7H8z",
  },
  { view: "certasset", to: "/certification/assets", label: "By asset", icon: "M3 7h18v13H3zM3 7l3-4h12l3 4M9 12h6" },
  {
    view: "certdue",
    to: "/certification/renewals",
    label: "Renewals",
    icon: "M8 2v4M16 2v4M3 10h18M5 4h14v16H5zM12 13v3l2 1",
  },
  {
    view: "certdocs",
    to: "/certification/documents",
    label: "Certificates on file",
    icon: "M6 3h9l5 5v13H6zM15 3v5h5M9 14l2 2 4-4",
  },
  /* business */
  { view: "exec", to: "/executive-kpis", label: "Executive KPIs", icon: "M4 20V10M10 20V4M16 20v-8M22 20H2" },
  {
    view: "finance",
    to: "/financials",
    label: "Financials",
    icon: "M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  },
  { view: "approvals", to: "/approvals", label: "Approvals", icon: "M5 4h14v16H5zM8 12l3 3 5-6" },
  /*
   * The stores desk and this board are one screen: the register on top, the
   * board's reading of it underneath. Named for what it is rather than for
   * the movement of goods, which is what "Logistics" described when the board
   * had no store behind it.
   */
  { view: "stores", to: "/logistics", label: "Store & Warehouse", icon: "M3 8l9-5 9 5v8l-9 5-9-5zM3 8l9 5 9-5M12 13v8" },
  {
    view: "sustain",
    to: "/sustainability",
    label: "Sustainability",
    icon: "M6 20c0-8 4-13 13-14-1 9-6 13-13 14zM6 20c2-4 5-7 9-9",
  },
  /* platform */
  { view: "analysis", to: "/intelligence", label: "Systems intelligence", icon: "M3 17l5-5 4 4 8-9M14 7h6v6" },
  { view: "diag", to: "/diagnostics", label: "System diagnostics", icon: "M3 12h4l2-5 4 10 2-5h6" },
  { view: "find", to: "/search", label: "Search", icon: "M10 4a6 6 0 110 12 6 6 0 010-12zM20 20l-5.5-5.5" },
  { view: "ask", to: "/ask", label: "Ask Sky-IQ", icon: "M12 3a8 8 0 00-7 12l-1 5 5-1a8 8 0 103-16zM12 8v5M12 16v1" },
];
